import React from 'react';
import {
  Container,
  Grid,
  Box,
  Paper,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const BestSellerCardSkeleton = ({ isMobile }) => (
  <Box
    sx={{
      borderRadius: { xs: 2.5, md: 3.5 },
      p: { xs: 1, md: 1.5 },
      background: 'rgba(255, 255, 255, 0.95)',
      border: '1px solid rgba(0, 0, 0, 0.05)',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
      height: '100%',
    }}
  >
    <Box sx={{ borderRadius: { xs: 2, md: 3 }, overflow: 'hidden' }}>
      <Skeleton height={isMobile ? 140 : 280} style={{ display: 'block', lineHeight: 1 }} />
    </Box>
    <Box sx={{ mt: { xs: 1, md: 3 }, px: { xs: 1, md: 2.5 }, pb: { xs: 0.5, md: 0 }, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ flex: 1 }}>
        <Skeleton height={isMobile ? 16 : 22} width="85%" style={{ marginBottom: isMobile ? 6 : 12 }} />
        <Skeleton height={isMobile ? 16 : 24} width="50%" />
      </Box>
      <Skeleton circle height={isMobile ? 36 : 56} width={isMobile ? 36 : 56} />
    </Box>
  </Box>
);

const ListItemSkeleton = ({ isMobile }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      p: { xs: 2, md: 3.5 },
      mb: { xs: 2, md: 3 },
      borderRadius: { xs: 2.5, md: 3.5 },
      background: 'rgba(255, 255, 255, 0.95)',
      border: '1px solid rgba(0, 0, 0, 0.06)',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    }}
  >
    <Box sx={{ flexShrink: 0, mr: { xs: 2, md: 3.5 } }}>
      <Skeleton
        width={isMobile ? 90 : 130}
        height={isMobile ? 90 : 130}
        style={{ borderRadius: isMobile ? 16 : 24, display: 'block', lineHeight: 1 }}
      />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <Skeleton height={isMobile ? 16 : 22} width="70%" style={{ marginBottom: 4 }} />
          <Skeleton height={isMobile ? 16 : 20} width="35%" />
        </Box>
        <Skeleton circle height={isMobile ? 36 : 44} width={isMobile ? 36 : 44} />
      </Box>
      <Skeleton height={isMobile ? 12 : 14} count={2} style={{ marginBottom: 2 }} />
      <Box sx={{ mt: 0.5 }}>
        <Skeleton height={isMobile ? 24 : 34} width={70} style={{ borderRadius: 20 }} />
      </Box>
    </Box>
  </Box>
);

const MenuPageSkeleton = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <SkeletonTheme baseColor="#f0f0f0" highlightColor="#e8e8e8">
      <Box sx={{ pb: 10, minHeight: '100vh', background: '#f8fafc' }}>
        {/* Cover Banner Skeleton */}
        <Box
          sx={{
            width: '100%',
            minHeight: { xs: 320, md: 480 },
            background: '#e8e8e8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <Box sx={{ textAlign: 'center', px: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Skeleton
                height={isMobile ? 44 : 64}
                width={isMobile ? 220 : 380}
                style={{ borderRadius: 12 }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Skeleton
                height={isMobile ? 18 : 24}
                width={isMobile ? 180 : 320}
                style={{ borderRadius: 8 }}
              />
            </Box>
          </Box>
        </Box>

        {/* Sticky Header Skeleton */}
        <Paper
          elevation={0}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(35px)',
            borderRadius: 0,
            py: { xs: 2, sm: 3.5 },
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 3 },
              alignItems: { xs: 'stretch', sm: 'center' }
            }}>
              {/* Search Bar */}
              <Box sx={{ flex: 1 }}>
                <Skeleton height={isMobile ? 40 : 56} style={{ borderRadius: 14 }} />
              </Box>
              {/* Category Dropdown + Action Buttons */}
              <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}>
                <Box sx={{ flex: { xs: 1, sm: 'none' }, minWidth: { xs: 0, sm: 190 } }}>
                  <Skeleton height={isMobile ? 40 : 56} style={{ borderRadius: 14 }} />
                </Box>
                <Skeleton width={isMobile ? 40 : 48} height={isMobile ? 40 : 48} style={{ borderRadius: isMobile ? 10 : 14, flexShrink: 0 }} />
              </Box>
            </Box>
          </Container>
        </Paper>

        <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
          {/* Best Sellers Section Skeleton */}
          <Box mb={8}>
            <Box sx={{ mb: { xs: 4, sm: 6 }, textAlign: 'center' }}>
              <Skeleton
                height={isMobile ? 28 : 48}
                width={isMobile ? 200 : 340}
                style={{ margin: '0 auto', borderRadius: 8 }}
              />
            </Box>
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 4 }} sx={{ mb: { xs: 4, sm: 6 } }}>
              {Array(isMobile ? 4 : 6).fill(0).map((_, i) => (
                <Grid item xs={6} sm={6} md={4} key={i}>
                  <BestSellerCardSkeleton isMobile={isTablet} />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Category Sections Skeleton */}
          {Array(2).fill(0).map((_, catIndex) => (
            <Box key={catIndex} mb={8}>
              <Box sx={{ mb: { xs: 3, sm: 5 }, textAlign: 'left' }}>
                <Skeleton
                  height={isMobile ? 24 : 36}
                  width={isMobile ? 140 : 220}
                  style={{ borderRadius: 8 }}
                />
              </Box>
              {Array(3).fill(0).map((_, itemIndex) => (
                <ListItemSkeleton key={itemIndex} isMobile={isTablet} />
              ))}
            </Box>
          ))}
        </Container>
      </Box>
    </SkeletonTheme>
  );
};

export default MenuPageSkeleton;
